/**
 * On-screen transcript layout (matches admin TranscriptDocument styling).
 * View-only — no print / PDF actions.
 */
import { useMemo } from "react";

const DOCUMENT_TITLE = "Student's Cumulative Record and Partial Transcript";
const OFFICE_TITLE = "Office of the Registrar";
const GRADING_SYSTEM_LEGEND = [
  { range: "95-100", grade: "A+" },
  { range: "90-94", grade: "A" },
  { range: "85-89", grade: "A-" },
  { range: "80-84", grade: "B+" },
  { range: "75-79", grade: "B" },
  { range: "70-74", grade: "B-" },
  { range: "65-69", grade: "C+" },
  { range: "60-64", grade: "C" },
  { range: "55-59", grade: "D+" },
  { range: "50-54", grade: "D" },
  { range: "Below 50", grade: "F" },
];

const SEMESTER_HEAD_BG = "#e8e8e8";
const MAROON = "#a53851";

function semesterOrdinal(semester, semOrderMap) {
  const s = String(semester).trim();
  if (semOrderMap && Object.prototype.hasOwnProperty.call(semOrderMap, s)) {
    return semOrderMap[s];
  }
  const semNum = /semester\s*(\d+)/i.exec(s);
  if (semNum) return 100 + Number(semNum[1]);
  const onlyDigits = /^(\d+)$/.exec(s);
  if (onlyDigits) return 100 + Number(onlyDigits[1]);
  const legacy = { spring: 1, summer: 2, fall: 3, winter: 4 };
  const low = s.toLowerCase();
  if (legacy[low] != null) return legacy[low];
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return 5000 + Math.abs(h % 10000);
}

function parseYearSemesterKey(key) {
  const i = key.indexOf("-");
  if (i <= 0) return { year: 0, semester: key };
  const y = Number(key.slice(0, i));
  return {
    year: Number.isFinite(y) ? y : 0,
    semester: key.slice(i + 1),
  };
}

function compareTranscriptSemesterKeys(a, b, semOrderMap) {
  const pa = parseYearSemesterKey(a);
  const pb = parseYearSemesterKey(b);
  if (pa.year !== pb.year) return pa.year - pb.year;
  return (
    semesterOrdinal(pa.semester, semOrderMap) -
    semesterOrdinal(pb.semester, semOrderMap)
  );
}

const SEMESTER_NUMBER_WORDS = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
];

function formatSemesterWords(semesterLabel) {
  const s = String(semesterLabel).trim();
  const m = /semester\s*(\d+)/i.exec(s);
  if (m) {
    const n = Number(m[1]);
    const w = SEMESTER_NUMBER_WORDS[n];
    if (w) return `Semester ${w}`;
    return `Semester ${m[1]}`;
  }
  return s.length > 0 ? s : "—";
}

function formatAcademicYearSemesterLine(yearStart, yearEnd, semesterLabel) {
  return `Academic Year: ${yearStart}-${yearEnd}. ${formatSemesterWords(
    semesterLabel
  )}`;
}

function earliestYearFromKeys(semesterKeys) {
  let min = Infinity;
  for (const k of semesterKeys) {
    const { year } = parseYearSemesterKey(k);
    if (year > 0 && year < min) min = year;
  }
  return min === Infinity ? null : min;
}

function yearFromAdmissionDate(d) {
  if (!d) return null;
  const s = typeof d === "string" ? d.trim() : new Date(d).toISOString().slice(0, 10);
  const iso = /^(\d{4})-\d{2}-\d{2}/.exec(s);
  if (iso) return Number(iso[1]);
  const t = new Date(d).getTime();
  if (Number.isNaN(t)) return null;
  return new Date(d).getFullYear();
}

export function PortalTranscriptDocument({
  student,
  examinationRecords,
  transcript,
  semesterSortMap,
  apiBase,
}) {
  const { recordsBySemester, semesterKeys, semGpaMap, cumulativeGPA, totalCredits } =
    useMemo(() => {
      const records = Array.isArray(examinationRecords) ? examinationRecords : [];
      const bySem = records.reduce((acc, r) => {
        const key = `${r.year}-${r.semester}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(r);
        return acc;
      }, {});

      const keys = Object.keys(bySem).sort((a, b) =>
        compareTranscriptSemesterKeys(a, b, semesterSortMap)
      );

      const gpaMap = {};
      for (const s of transcript?.semesters ?? []) {
        gpaMap[`${s.year}-${s.semester}`] = s;
      }

      return {
        recordsBySemester: bySem,
        semesterKeys: keys,
        semGpaMap: gpaMap,
        cumulativeGPA: transcript?.cumulativeGPA ?? 0,
        totalCredits: transcript?.totalCredits ?? 0,
      };
    }, [examinationRecords, transcript, semesterSortMap]);

  const college = student?.department?.faculty?.name ?? "—";
  const department = student?.department?.name ?? "—";
  const studentName = `${student?.firstName ?? ""} ${student?.lastName ?? ""}`.trim();
  const fromGrades = earliestYearFromKeys(semesterKeys);
  const entryYear =
    fromGrades != null
      ? String(fromGrades)
      : yearFromAdmissionDate(student?.admissionDate) != null
        ? String(yearFromAdmissionDate(student?.admissionDate))
        : "—";

  const legendHeading = "Grading System:";

  return (
    <div
      className="mx-auto max-w-[210mm] border border-slate-200 bg-white px-3 py-4 text-black shadow-sm sm:px-4"
      style={{ color: "#000" }}
    >
      <p className="mb-3 rounded-md bg-slate-100 px-2 py-1.5 text-center text-[10px] font-medium text-slate-600 sm:text-xs">
        On-screen academic record — not a downloadable official PDF. For
        verification, contact the registrar.
      </p>

      <h2 className="mb-3 text-center text-sm font-bold sm:text-[13px]">
        {DOCUMENT_TITLE}
      </h2>

      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:flex-nowrap sm:items-stretch sm:gap-0">
        <div className="min-w-0 w-full flex-1">
          <table
            className="h-full w-full border border-black text-[10px] sm:text-[11px]"
            style={{ borderCollapse: "collapse" }}
          >
            <tbody>
              {[
                ["College", college],
                ["Department", department],
                ["Student Name", studentName],
                ["Student ID", student?.studentId ?? "—"],
                ["Entry Year", entryYear],
              ].map(([label, value]) => (
                <tr key={label}>
                  <td
                    className="w-[30%] border border-black px-2 py-2 text-[10px] font-bold sm:text-[11px]"
                  >
                    {label}
                  </td>
                  <td
                    className={`border border-black px-2 py-2 text-[10px] font-bold sm:text-[11px] ${
                      label === "Student ID" ? "font-mono" : ""
                    }`}
                  >
                    {value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <table
          className="w-full shrink-0 border border-black text-[10px] sm:w-44 sm:text-[11px]"
          style={{ borderCollapse: "collapse" }}
        >
          <thead>
            <tr>
              <th colSpan={2} className="border border-black bg-white px-1 py-0.5 text-left font-bold">
                {legendHeading}
              </th>
            </tr>
            <tr>
              <th className="border border-black bg-white px-1 py-0.5 text-center font-semibold">
                Percentage Score
              </th>
              <th className="border border-black bg-white px-1 py-0.5 text-center font-semibold">
                Grade
              </th>
            </tr>
          </thead>
          <tbody>
            {GRADING_SYSTEM_LEGEND.map(({ range, grade }) => (
              <tr key={`${range}-${grade}`}>
                <td className="border border-black px-1 py-0.5">{range}</td>
                <td className="border border-black px-1 py-0.5 text-center font-semibold">
                  {grade}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {semesterKeys.length === 0 ? (
        <div className="space-y-2 py-6 text-center text-sm text-slate-600">
          <p>No course marks are stored in the system for this student yet.</p>
          <p className="mx-auto max-w-md text-xs leading-relaxed">
            A PDF transcript may be from an older export. The portal only shows
            courses entered under <strong>Examinations</strong> in the admin app.
            Ask the registrar to import or record marks if they should appear here.
          </p>
        </div>
      ) : (
        semesterKeys.map((key, keyIdx) => {
          const { year: yearEndNum, semester: semesterLabel } =
            parseYearSemesterKey(key);
          const recs = recordsBySemester[key] || [];
          const semGpa = semGpaMap[key];

          const prevKeys = semesterKeys.slice(0, keyIdx);
          let cumCredits = 0;
          let cumHpts = 0;
          for (const pk of prevKeys) {
            const pr = recordsBySemester[pk] || [];
            for (const r of pr) {
              cumCredits += r.course?.creditHours ?? 0;
              cumHpts +=
                (r.course?.creditHours ?? 0) * (r.gradePoints ?? 0);
            }
          }
          const thisSemCredits = recs.reduce(
            (s, r) => s + (r.course?.creditHours ?? 0),
            0
          );
          const thisSemHpts = recs.reduce(
            (s, r) =>
              s + (r.course?.creditHours ?? 0) * (r.gradePoints ?? 0),
            0
          );
          const totalCreditsSoFar = cumCredits + thisSemCredits;
          const totalHptsSoFar = cumHpts + thisSemHpts;
          const cgpa =
            totalCreditsSoFar > 0
              ? Math.round((totalHptsSoFar / totalCreditsSoFar) * 100) / 100
              : 0;

          const yearEnd = yearEndNum;
          const yearStart = yearEnd - 1;
          const academicSemesterLine = formatAcademicYearSemesterLine(
            yearStart,
            yearEnd,
            semesterLabel
          );

          const colWidths = ["16%", "36%", "8%", "14%", "10%", "16%"];

          return (
            <div key={key} className="mb-3 last:mb-1">
              <div className="border border-black">
               <div
                  className="border-b border-black px-2 py-1.5 text-left text-[11px] font-bold underline sm:text-[12px]"
                  style={{ backgroundColor: SEMESTER_HEAD_BG }}
                >
                  {academicSemesterLine}
                </div>
                <table
                  className="mt-0 w-full table-fixed text-[10px] sm:text-[11px]"
                  style={{ borderCollapse: "collapse", tableLayout: "fixed" }}
                >
                  <colgroup>
                    {colWidths.map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      <th
                        className="border border-black px-0.5 py-0.5 text-left font-bold text-white"
                        style={{ backgroundColor: MAROON }}
                      >
                        Course Code
                      </th>
                      <th
                        className="border border-black px-0.5 py-0.5 text-left font-bold text-white"
                        style={{ backgroundColor: MAROON }}
                      >
                        Course Title
                      </th>
                      <th
                        className="border border-black px-0.5 py-0.5 text-center font-bold text-white"
                        style={{ backgroundColor: MAROON }}
                      >
                        CrHrs
                      </th>
                      <th
                        className="border border-black px-0.5 py-0.5 text-center font-bold text-white"
                        style={{ backgroundColor: MAROON }}
                      >
                        Marks
                      </th>
                      <th
                        className="border border-black px-0.5 py-0.5 text-center font-bold text-white"
                        style={{ backgroundColor: MAROON }}
                      >
                        Grade
                      </th>
                      <th
                        className="border border-black px-0.5 py-0.5 text-center font-bold text-white"
                        style={{ backgroundColor: MAROON }}
                      >
                        GPA
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {recs.map((r) => (
                      <tr key={r.id} className="bg-white">
                        <td className="border border-black px-0.5 py-0.5 text-left font-mono font-semibold wrap-break-word">
                          {r.course?.code}
                        </td>
                        <td className="border border-black px-0.5 py-0.5 text-left wrap-break-word">
                          {r.course?.name}
                        </td>
                        <td className="border border-black px-0.5 py-0.5 text-center">
                          {r.course?.creditHours}
                        </td>
                        <td className="border border-black px-0.5 py-0.5 text-center">
                          {Number(r.totalMarks ?? 0).toFixed(2)}
                        </td>
                        <td className="border border-black px-0.5 py-0.5 text-center font-semibold">
                          {r.grade || "—"}
                        </td>
                        <td className="border border-black px-0.5 py-0.5 text-center">
                          {r.gradePoints != null
                            ? Number(r.gradePoints).toFixed(2)
                            : "0.00"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td
                        colSpan={2}
                        className="border border-black px-0.5 py-0.5 font-semibold"
                      >
                        Total
                      </td>
                      <td className="border border-black px-0.5 py-0.5 text-center font-bold">
                        {thisSemCredits}
                      </td>
                      <td className="border border-black px-0.5 py-0.5" />
                      <td className="border border-black px-0.5 py-0.5" />
                      <td className="border border-black px-0.5 py-0.5" />
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="mt-1 flex flex-wrap items-baseline justify-end gap-x-4 text-[10px] font-semibold sm:text-[11px]">
                <span>
                  GPA:{" "}
                  <span className="font-bold">
                    {semGpa?.gpa != null
                      ? Number(semGpa.gpa).toFixed(2)
                      : "0.00"}
                  </span>
                </span>
                {keyIdx > 0 ? (
                  <span>
                    CGPA: <span className="font-bold">{cgpa.toFixed(2)}</span>
                  </span>
                ) : null}
              </div>
            </div>
          );
        })
      )}

      <div className="mt-3 border-t border-black pt-2">
        <p className="text-[11px] font-bold">
          Cumulative GPA:{" "}
          <span className="text-[12px]">{cumulativeGPA.toFixed(2)}</span>
        </p>
        <p className="text-[11px]">Total Credits: {totalCredits}</p>
        <p className="mt-1 text-[10px] text-slate-600">
          Generated: {new Date().toLocaleDateString()}
        </p>
      </div>

      <div className="mt-4 flex justify-end">
        <div className="w-40 text-right">
          <div className="mb-0.5 border-t-2 border-black" />
          <p className="text-[11px] font-bold">{OFFICE_TITLE}</p>
          <p className="text-[10px] text-slate-600">Authorized Signature</p>
        </div>
      </div>
    </div>
  );
}
